const configMissingExceptionMessage = (propName: string) =>
  `Failed to provide required configured value '${propName}'.`;
const configParseExceptionMessage = (propName: string) =>
  `Failed to parse configured value '${propName}' to the expected type.`;
const version = import.meta.env.VITE_VERSION_NO;
if (!version) throw configMissingExceptionMessage("VITE_VERSION_NO");

const environmentName = import.meta.env.VITE_ENVNAME;
if (!environmentName) throw configMissingExceptionMessage("VITE_ENVNAME");

const a1cMaximum = import.meta.env.VITE_MAXIMUM_A1C;
if (!a1cMaximum) throw configMissingExceptionMessage("VITE_MAXIMUM_A1C");
const a1cUpperLimit = Number(a1cMaximum);
if (isNaN(a1cUpperLimit)) throw configParseExceptionMessage("VITE_MAXIMUM_A1C");

const convertDotComAccountId = import.meta.env.VITE_CONVERT_DOT_COM_ACCOUNT_ID;
if (!convertDotComAccountId) throw configMissingExceptionMessage("VITE_CONVERT_DOT_COM_ACCOUNT_ID");

const convertDotComProjectId = import.meta.env.VITE_CONVERT_DOT_COM_PROJECT_ID;
if (!convertDotComProjectId) throw configMissingExceptionMessage("VITE_CONVERT_DOT_COM_PROJECT_ID");

const convertDotComDefaultVariationIdsStringified =
  import.meta.env.VITE_CONVERT_DOT_COM_DEFAULT_VARIATION_IDS?.toLowerCase();
if (!convertDotComDefaultVariationIdsStringified)
  throw configMissingExceptionMessage("VITE_CONVERT_DOT_COM_DEFAULT_VARIATION_IDS");
const convertDotComDefaultVariationIds = JSON.parse(convertDotComDefaultVariationIdsStringified) as string[];
if (convertDotComDefaultVariationIds.constructor !== Array)
  throw configParseExceptionMessage("VITE_CONVERT_DOT_COM_DEFAULT_VARIATION_IDS");
if (!convertDotComDefaultVariationIds.every(id => typeof id === "string"))
  throw new Error(
    `VITE_CONVERT_DOT_COM_DEFAULT_VARIATION_IDS must be an array of strings (e.g., ["1004483292"]). ` +
      `Received: ${JSON.stringify(convertDotComDefaultVariationIds)}`
  );

const escapePhoneNumber = import.meta.env.VITE_ESCAPE_PHONE_NUMBER;
if (!escapePhoneNumber) throw configMissingExceptionMessage("VITE_ESCAPE_PHONE_NUMBER");

const escapeUrl = import.meta.env.VITE_ESCAPE_URL;
if (!escapeUrl) throw configMissingExceptionMessage("VITE_ESCAPE_URL");

const aspireBaseUrl = import.meta.env.VITE_ASPIRE_API_URL;
if (!aspireBaseUrl) throw configMissingExceptionMessage("VITE_ASPIRE_API_URL");

const fallbackPatientDistanceToCenter = Number(import.meta.env.VITE_FALLBACK_PATIENT_DISTANCE_TO_CENTER);
if (isNaN(fallbackPatientDistanceToCenter))
  throw configParseExceptionMessage("VITE_FALLBACK_PATIENT_DISTANCE_TO_CENTER");

const anonymousExperimentsFlag = import.meta.env.VITE_ANONYMOUS_EXPERIMENTS;
if (!anonymousExperimentsFlag) throw configMissingExceptionMessage("VITE_ANONYMOUS_EXPERIMENTS");

const forceExperimentalFlowVariantFlag = import.meta.env.VITE_FORCE_EXPERIMENTAL_FLOW_VARIANT;
if (!forceExperimentalFlowVariantFlag) throw configMissingExceptionMessage("VITE_FORCE_EXPERIMENTAL_FLOW_VARIANT");

const forcePatientUnqualifiedFlag = import.meta.env.VITE_FORCE_PATIENT_UNQUALIFIED;
if (!forcePatientUnqualifiedFlag) throw configMissingExceptionMessage("VITE_FORCE_PATIENT_UNQUALIFIED");

const disablePatientQualificationReportingFlag = import.meta.env.VITE_DISABLE_PATIENT_QUALIFICATION_REPORTING;
if (!disablePatientQualificationReportingFlag)
  throw configMissingExceptionMessage("VITE_DISABLE_PATIENT_QUALIFICATION_REPORTING");

const reservationLifespanSeconds = Number(import.meta.env.VITE_RESERVATION_LIFESPAN_SECONDS);
if (isNaN(reservationLifespanSeconds)) throw configParseExceptionMessage("VITE_RESERVATION_LIFESPAN_SECONDS");

const calendarAllowedPaddingMinutes = Number(import.meta.env.VITE_CALENDAR_ALLOWED_PADDING_MINUTES);
if (isNaN(calendarAllowedPaddingMinutes)) throw configParseExceptionMessage("VITE_CALENDAR_ALLOWED_PADDING_MINUTES");

const minimumAge = Number(import.meta.env.VITE_MINIMUM_AGE);
if (isNaN(minimumAge)) throw configParseExceptionMessage("VITE_MINIMUM_AGE");

const minimumBodyMassIndex = Number(import.meta.env.VITE_MINIMUM_BODY_MASS_INDEX);
if (isNaN(minimumBodyMassIndex)) throw configParseExceptionMessage("VITE_MINIMUM_BODY_MASS_INDEX");

const maximumBodyMassIndex = Number(import.meta.env.VITE_MAXIMUM_BODY_MASS_INDEX);
if (isNaN(maximumBodyMassIndex)) throw configParseExceptionMessage("VITE_MAXIMUM_BODY_MASS_INDEX");

const thankYouUrl = import.meta.env.VITE_THANK_YOU_URL;
if (!thankYouUrl) throw configMissingExceptionMessage("VITE_THANK_YOU_URL");

const gtmEnabled = import.meta.env.VITE_GTM_ENABLED === "true";
const gtmContainerId = import.meta.env.VITE_GTM_CONTAINER_ID;
const gtmAuth = import.meta.env.VITE_GTM_AUTH;
const gtmPreview = import.meta.env.VITE_GTM_PREVIEW;
const gtmCookiesWin = import.meta.env.VITE_GTM_COOKIES_WIN;

const singleMedicalStepFlowExperimentKey = import.meta.env.VITE_SINGLE_MEDICAL_STEP_FLOW_EXPERIMENT_KEY;
if (!singleMedicalStepFlowExperimentKey)
  throw configMissingExceptionMessage("VITE_SINGLE_MEDICAL_STEP_FLOW_EXPERIMENT_KEY");

const convertDotComWebProjectId = import.meta.env.VITE_CONVERT_DOT_COM_WEB_PROJECT_ID;
if (!convertDotComWebProjectId) throw configMissingExceptionMessage("VITE_CONVERT_DOT_COM_WEB_PROJECT_ID");

const convertDotComWebGoalId = import.meta.env.VITE_CONVERT_DOT_COM_WEB_GOAL_ID;
if (!convertDotComWebGoalId) throw configMissingExceptionMessage("VITE_CONVERT_DOT_COM_WEB_GOAL_ID");

/** The environment configuration for the current  application instance.
 * @remarks GUID values are all cast to lower case.
 */
const EnvironmentConfiguration = {
  a1cUpperLimit,
  aspireBaseUrl,
  anonymousExperiments: anonymousExperimentsFlag === "true",
  forceExperimentalFlowVariant: forceExperimentalFlowVariantFlag === "true",
  forcePatientUnqualified: forcePatientUnqualifiedFlag === "true",
  disablePatientQualificationReporting: disablePatientQualificationReportingFlag === "true",
  escapePhoneNumber,
  escapeUrl,
  convertDotCom: {
    accountId: convertDotComAccountId,
    projectId: convertDotComProjectId,
    defaultVariationIds: convertDotComDefaultVariationIds,
    singleMedicalStepFlowExperimentKey,
    // second Convert project used for the hand-rolled REST goal-tracking prototype (no SDK support); shares accountId
    webGoalTracking: {
      projectId: convertDotComWebProjectId,
      goalId: convertDotComWebGoalId
    }
  },
  name: environmentName,
  optimizedPatientScheduler: {
    fallbackPatientDistanceToCenter,
    notSupportedCenterIds: [] as string[]
  },
  reservationLifespanSeconds,
  version,
  calendarAllowedPaddingMinutes,
  minimumAge,
  minimumBodyMassIndex,
  maximumBodyMassIndex,
  thankYouUrl,
  googleTagsManager: {
    enabled: gtmEnabled,
    containerId: gtmContainerId,
    auth: gtmAuth,
    preview: gtmPreview,
    cookiesWin: gtmCookiesWin
  }
};

export default EnvironmentConfiguration;
